import instagramLogo from '@/assets/images/instagram.svg'
import facebookLogo from '@/assets/images/facebook.svg'
import linkedinLogo from '@/assets/images/linkedin.svg'
import tiktokLogo from '@/assets/images/tiktok.svg'

const svg = (fill: string, path: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}" style="width:100%;height:100%">${path}</svg>`

const paths = {
  twitter:   `<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L2.25 2.25H8.08l4.261 5.636 5.903-5.636zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>`,
  youtube:   `<path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 00.5 6.2 31.3 31.3 0 000 12a31.3 31.3 0 00.5 5.8 3 3 0 002.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 002.1-2.1c.3-1.9.5-3.8.5-5.8a31.3 31.3 0 00-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/>`,
  pinterest: `<path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>`,
}

export interface Platform {
  id: string
  label: string
  logo: string | null
  svgNormal: string | null
  svgWhite: string | null
  color: string
}

export const PLATFORMS: Platform[] = [
  { id: 'instagram', label: 'Instagram', logo: instagramLogo, svgNormal: null, svgWhite: null, color: 'linear-gradient(45deg,#833ab4,#fd1d1d,#fcb045)' },
  { id: 'facebook',  label: 'Facebook',  logo: facebookLogo,  svgNormal: null, svgWhite: null, color: '#1877F2' },
  { id: 'twitter',   label: 'Twitter / X', logo: null, svgNormal: svg('#000', paths.twitter), svgWhite: svg('white', paths.twitter), color: '#000000' },
  { id: 'linkedin',  label: 'LinkedIn',  logo: linkedinLogo,  svgNormal: null, svgWhite: null, color: '#0A66C2' },
  { id: 'tiktok',    label: 'TikTok',    logo: tiktokLogo,    svgNormal: null, svgWhite: null, color: '#010101' },
  { id: 'youtube',   label: 'YouTube',   logo: null, svgNormal: svg('#FF0000', paths.youtube),  svgWhite: svg('white', paths.youtube),  color: '#FF0000' },
  { id: 'pinterest', label: 'Pinterest', logo: null, svgNormal: svg('#E60023', paths.pinterest), svgWhite: svg('white', paths.pinterest), color: '#E60023' },
]

export const getPlatform = (id: string): Platform | undefined =>
  PLATFORMS.find(p => p.id === id)

export const PLATFORM_IDS = PLATFORMS.map(p => p.id)

export const getPlatformStyle = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'instagram': return 'bg-gradient-to-br from-purple-600 via-pink-600 to-yellow-500 text-white'
    case 'facebook':  return 'bg-blue-600 text-white'
    case 'tiktok':    return 'bg-black text-white'
    case 'twitter':   return 'bg-sky-500 text-white'
    case 'linkedin':  return 'bg-blue-700 text-white'
    case 'youtube':   return 'bg-red-600 text-white'
    case 'pinterest': return 'bg-red-500 text-white'
    default:          return 'bg-gray-500 text-white'
  }
}

export const getPlatformBackgroundClass = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'instagram': return 'bg-gradient-to-r from-purple-500 to-pink-500'
    case 'facebook':  return 'bg-blue-600'
    case 'tiktok':    return 'bg-black dark:bg-white'
    case 'twitter':   return 'bg-blue-400'
    case 'linkedin':  return 'bg-blue-700'
    case 'youtube':   return 'bg-red-600'
    case 'pinterest': return 'bg-red-500'
    default:          return 'bg-gray-500'
  }
}

export const getPlatformColor = getPlatformBackgroundClass

export const getPlatformActiveClass = (platform: string): string => ({
  instagram: 'bg-pink-50 border-pink-300 text-pink-700',
  facebook:  'bg-blue-50 border-blue-300 text-blue-700',
  tiktok:    'bg-gray-100 border-gray-400 text-gray-800',
  twitter:   'bg-sky-50 border-sky-300 text-sky-700',
  linkedin:  'bg-blue-50 border-blue-300 text-blue-700',
  youtube:   'bg-red-50 border-red-300 text-red-700',
  pinterest: 'bg-rose-50 border-rose-300 text-rose-700',
}[platform] || 'bg-gray-50 border-gray-200 text-gray-700')

export const getPlatformDotClass = (p: string): string => ({
  instagram: 'bg-pink-500',
  facebook:  'bg-blue-600',
  tiktok:    'bg-gray-800',
  twitter:   'bg-sky-500',
  linkedin:  'bg-blue-700',
  youtube:   'bg-red-600',
  pinterest: 'bg-red-500',
}[p] || 'bg-gray-400')

export const getPlatformBadgeStyle = (platform: string): string => {
  switch (platform.toLowerCase()) {
    case 'instagram': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
    case 'facebook':  return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    case 'twitter':   return 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300'
    case 'linkedin':  return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
    case 'youtube':   return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    case 'pinterest': return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300'
    default:          return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  }
}

export const CHARACTER_LIMITS: Record<string, number> = {
  instagram: 2200,
  facebook:  63206,
  tiktok:    4000,
  twitter:   280,
  linkedin:  3000,
  youtube:   5000,
  pinterest: 500,
}

export const getCharLimit = (platform: string): number =>
  CHARACTER_LIMITS[platform] ?? 2200

export const PLATFORM_COLORS: Record<string, string> = {
  instagram: '#e1306c',
  facebook:  '#1877f2',
  tiktok:    '#010101',
  twitter:   '#1da1f2',
  linkedin:  '#0a66c2',
  youtube:   '#ff0000',
  pinterest: '#e60023',
}

export interface PlatformCanvasTemplate {
  id: string
  name: string
  size: string
  width: number
  height: number
  icon: string
}

export const PLATFORM_CANVAS_TEMPLATES: PlatformCanvasTemplate[] = [
  { id: 'instagram-square',    name: 'Instagram Square',    size: '1080x1080', width: 1080, height: 1080, icon: 'instagram' },
  { id: 'instagram-story',     name: 'Instagram Story',     size: '1080x1920', width: 1080, height: 1920, icon: 'instagram' },
  { id: 'facebook-post',       name: 'Facebook Post',       size: '1200x630',  width: 1200, height: 630,  icon: 'facebook'  },
  { id: 'twitter-post',        name: 'Twitter Post',        size: '1200x675',  width: 1200, height: 675,  icon: 'twitter'   },
  { id: 'linkedin-post',       name: 'LinkedIn Post',       size: '1200x627',  width: 1200, height: 627,  icon: 'linkedin'  },
  { id: 'youtube-thumbnail',   name: 'YouTube Thumbnail',   size: '1280x720',  width: 1280, height: 720,  icon: 'youtube'   },
]

export const PLATFORM_HASHTAG_MAP: Record<string, string[]> = {
  instagram: ['instagood', 'photooftheday', 'reels', 'explore', 'contentcreator'],
  twitter:   ['trending', 'viral', 'thread', 'news', 'twittermarketing'],
  linkedin:  ['leadership', 'innovation', 'networking', 'career', 'growth'],
  facebook:  ['community', 'share', 'engage', 'local', 'events'],
  tiktok:    ['fyp', 'foryou', 'viral', 'trending', 'tiktokviral'],
  youtube:   ['youtube', 'video', 'subscribe', 'tutorial', 'howto'],
  pinterest: ['diy', 'recipes', 'homedecor', 'fashion', 'inspiration'],
  default:   ['socialmedia', 'digitalmarketing', 'brand', 'creative', 'business'],
}

export const PLATFORM_OPTIMAL_TIMES: { platform: string; time: string; tip: string }[] = [
  { platform: 'instagram', time: '6:00 PM – 9:00 PM',   tip: 'Highest engagement window' },
  { platform: 'facebook',  time: '1:00 PM – 3:00 PM',   tip: 'Lunch break scrollers' },
  { platform: 'tiktok',    time: '6:00 AM – 10:00 AM',  tip: 'Morning commute peak' },
  { platform: 'twitter',   time: '8:00 AM – 10:00 AM',  tip: 'Morning news readers' },
  { platform: 'linkedin',  time: '9:00 AM – 11:00 AM',  tip: 'Business hours start' },
  { platform: 'youtube',   time: '2:00 PM – 4:00 PM',   tip: 'After school / lunch' },
  { platform: 'pinterest', time: '8:00 PM – 11:00 PM',  tip: 'Evening browsing peak' },
]
